import { Award, BadgeCheck, Banknote, BarChart3, BedDouble, BookOpen, BookUser, Bot, Brain, Briefcase, Building2, Bus, Calculator, CalendarCheck, CalendarRange, ClipboardList, Clock3, DoorOpen, FileText, GraduationCap, HandCoins, HeartHandshake, HeartPulse, IndianRupee, Landmark, Library, ListChecks, MailPlus, Megaphone, MonitorCheck, NotebookPen, PlaneTakeoff, ReceiptIndianRupee as Receipt, Scale, ShieldCheck, Target, Trophy, UserPlus, Users, UsersRound } from "lucide-react";

/**
 * Semantic module → Lucide icon registry — the ONE place a module's glyph is decided.
 *
 * Audit finding: three independent icon pickers (nav, record registry, ad-hoc page imports)
 * chose glyphs separately, so the same module could render with colliding metaphors. New
 * surfaces must import from here (`MODULE_ICONS` / `iconFor`) instead of picking a Lucide
 * icon inline, so a metaphor can never fork again.
 *
 * PRD Phase-6 "18 school icons" → module keys in this registry:
 *   StudentIcon      → students      (GraduationCap — the learner)
 *   EnrollmentIcon   → enrollment    (GraduationCap — joining the school)
 *   AttendanceIcon   → attendance    (CalendarCheck — present-day clarity)
 *   TimetableIcon    → timetable     (CalendarRange — the week's rhythm)
 *   ProfileIcon      → students      (the whole child lives on the student record)
 *   DynamicFormIcon  → forms         (ListChecks — fillable fields)
 *   DocumentIcon     → documents     (FileText — records)
 *   CertificateIcon  → certificates  (Award — earned recognition)
 *   AchievementIcon  → achievements  (Trophy — celebration)
 *   DisciplineIcon   → discipline    (ShieldCheck — fairness/conduct)
 *   AlumniIcon       → alumni        (UsersRound — the batch beyond school)
 *   PTMIcon          → ptm           (UsersRound — parent–teacher meeting)
 *   TaskIcon         → tasks         (ListChecks — operational checklist)
 *   ParentIcon       → parent        (Users — guardian partnership)
 *   SchoolIcon       → school        (Building2 — the institution)
 *   AwardIcon        → achievements  (merit rolls up with achievements)
 *   IDCardIcon       → idcard        (BadgeCheck — student identity)
 *   CouncilIcon      → council       (Landmark — student governance/elections)
 */
export const MODULE_ICONS = {
  students: GraduationCap,
  admissions: Users,
  attendance: CalendarCheck,
  timetable: CalendarRange,
  "teaching-assignments": BookUser,
  exams: ClipboardList,
  fees: Receipt,
  accounting: Calculator,
  certificates: Award,
  achievements: Trophy,
  discipline: ShieldCheck,
  ptm: UsersRound,
  forms: ListChecks,
  documents: FileText,
  alumni: UsersRound,
  tasks: ListChecks,
  hr: Briefcase,
  teachers: BookUser,
  recruitment: UserPlus,
  onboarding: ListChecks,
  credentials: BadgeCheck,
  performance: Target,
  "learning-development": BookOpen,
  "employee-service": HeartHandshake,
  offboarding: DoorOpen,
  "hr-payroll-copilot": Bot,
  "workforce-analytics": BarChart3,
  "workforce-time": Clock3,
  "workforce-schedules": CalendarRange,
  "substitute-coverage": UsersRound,
  "workforce-workload": Scale,
  organisation: Building2,
  compensation: HandCoins,
  "employee-finance": IndianRupee,
  payroll: Banknote,
  "payroll-compliance": ShieldCheck,
  leave: PlaneTakeoff,
  transport: Bus,
  library: Library,
  hostel: BedDouble,
  health: HeartPulse,
  homework: BookOpen,
  lessonplan: NotebookPen,
  onlineexam: MonitorCheck,
  communication: Megaphone,
  "email-templates": MailPlus,
  reports: BarChart3,
  insights: Brain,
  idcard: BadgeCheck,
  council: Landmark,
  parent: Users,
  school: Building2,
  enrollment: GraduationCap,
};

/**
 * Resolve a module's icon component, with an explicit fallback for modules not (yet) in the
 * registry. Defaults to FileText (the neutral "record" glyph) so callers always get a component.
 *
 * @param {string} module - a MODULE_ICONS key, e.g. "students"
 * @param {React.ComponentType} [fallback] - icon to use when the module has no registered glyph
 * @returns {React.ComponentType} a Lucide icon component
 */
export function iconFor(module, fallback) {
  return MODULE_ICONS[module] || fallback || FileText;
}

/**
 * Semantic accent companion to MODULE_ICONS — the ONE place a module's colour meaning is decided.
 * Mirrors the SCHOOL_ICONS accents (components/ui/SchoolIcon.js) so a module's glyph AND its tint
 * agree everywhere (nav featured-icon chips, KPI FeaturedIcon, EmptyStates). Every value is a key
 * of primitives.js `_STAT_ACCENT` (primary/success/danger/saffron/sky/slate/ai/celebrate), so a
 * FeaturedIcon rendered from `accentFor(module)` re-themes with white-label + finance/AI scopes.
 *
 * Colour carries meaning, never decoration: attendance/parent/fees/accounting → positive (present/
 * collected), certificates/achievements → saffron (earned recognition), discipline/health → danger
 * (consequence/urgency), timetable/alumni/idcard/transport/library/hostel → sky (schedules/cohorts/
 * logistics), forms/documents/profile → slate (neutral records), insights → ai (purple), and the
 * student/admissions/tasks/ptm/school/communication core → primary (the brand).
 */
export const ACCENT_BY_MODULE = {
  students: "primary",
  enrollment: "primary",
  admissions: "primary",
  attendance: "success",
  timetable: "sky",
  "teaching-assignments": "sky",
  exams: "primary",
  fees: "success",
  accounting: "success",
  certificates: "saffron",
  achievements: "saffron",
  discipline: "danger",
  ptm: "primary",
  forms: "slate",
  documents: "slate",
  profile: "slate",
  alumni: "sky",
  tasks: "primary",
  hr: "primary",
  teachers: "primary",
  recruitment: "primary",
  onboarding: "success",
  credentials: "slate",
  performance: "saffron",
  "learning-development": "primary",
  "employee-service": "sky",
  offboarding: "slate",
  "hr-payroll-copilot": "ai",
  "workforce-analytics": "ai",
  "workforce-time": "success",
  "workforce-schedules": "sky",
  "substitute-coverage": "sky",
  "workforce-workload": "saffron",
  organisation: "primary",
  compensation: "success",
  "employee-finance": "success",
  payroll: "success",
  "payroll-compliance": "saffron",
  leave: "sky",
  transport: "sky",
  library: "sky",
  hostel: "sky",
  health: "danger",
  homework: "saffron",
  lessonplan: "primary",
  onlineexam: "sky",
  communication: "primary",
  "email-templates": "primary",
  reports: "primary",
  insights: "ai",
  idcard: "sky",
  council: "primary",
  parent: "success",
  school: "primary",
};

/**
 * Resolve a module's semantic accent (a `_STAT_ACCENT` / FeaturedIcon key). Defaults to "primary"
 * (the brand) for modules not in the registry, so every caller gets a valid, themeable accent.
 *
 * @param {string} module - an ACCENT_BY_MODULE key, e.g. "attendance"
 * @param {string} [fallback] - accent to use when the module has no registered accent
 * @returns {string} a semantic accent token
 */
export function accentFor(module, fallback = "primary") {
  return ACCENT_BY_MODULE[module] || fallback;
}
